import React, { type FC, type PropsWithChildren } from 'react';

import { graphql } from 'gatsby';
import { Box, Text, Link, LogoSection, Heading } from '@lucid/components-react';
import reactHTMLParser from 'html-react-parser';
import { getProcessedValue } from '@lucid/utils';
import {
  type Drupal_ParagraphExternalLogo,
  type Drupal_ParagraphFlexiCustomerLogos,
  type Drupal_ParagraphExternalLogoSection,
} from 'generated/drupal-graphql';
import { mqMin } from '@lucid/theme';
import type { Lucid, ExternalLogos } from 'types/types';
import FlourishedHeading from 'components/flourished-heading/flourished-heading';

interface FlexiCustomerLogosProps {
  /** Entity values from Drupal */
  entity: Drupal_ParagraphFlexiCustomerLogos;
  /** Determines whether the background should be dark */
  hasDarkBackground?: boolean;
  /** Determines whether the heading should go below the icons */
  hasHeadingBelow?: boolean;
  /** The heading variant */
  headingVariant?: Lucid.Component.Heading['Variant'] | Lucid.ThemeUI.Typography;
}

type HeaderJustify = 'left' | 'center' | 'right';

const FlexiCustomerLogos: FC<PropsWithChildren<FlexiCustomerLogosProps>> = ({
  entity: {
    fieldCustomerLogosHeader,
    fieldFlourishedHeading,
    fieldCustomerLogosLink,
    fieldLogosSection,
    fieldFlexiHeaderJustify,
  },
  hasDarkBackground,
  hasHeadingBelow,
  headingVariant = 'paragraphL300',
}) => {
  const headerJustify = fieldFlexiHeaderJustify?.entity?.entityLabel?.toLowerCase() as HeaderJustify;
  const customerLogoHeader = reactHTMLParser(getProcessedValue(fieldCustomerLogosHeader));
  const logoSection = fieldLogosSection?.entity as Drupal_ParagraphExternalLogoSection;
  const logos = logoSection?.fieldExternalLogos?.map(
    e => (e?.entity as Drupal_ParagraphExternalLogo)?.fieldLogoSlug,
  ) as ExternalLogos.LogoSlug[];
  return (
    <Box
      css={(theme: Lucid.Theme) => ({
        label: 'flexiCustomerLogos',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        width: '100%',
        marginTop: fieldFlourishedHeading ? theme.space.s40 : 0,
        marginBottom: hasDarkBackground ? 0 : theme.space.s104,
      })}
      gridColumn="1 / -1"
    >
      {fieldCustomerLogosHeader && fieldFlourishedHeading && !hasHeadingBelow && (
        <Box>
          <FlourishedHeading
            as="h2"
            css={(theme: Lucid.Theme) => ({
              marginTop: theme.space.s104,
              [mqMin.tabletLandscape]: {
                flex: '0 1 45%',
                maxWidth: '45%',
              },
            })}
            variant="headline3XL"
          >
            {customerLogoHeader}
          </FlourishedHeading>
        </Box>
      )}
      {fieldCustomerLogosHeader && !fieldFlourishedHeading && !hasHeadingBelow && (
        <Heading
          as="h2"
          css={(theme: Lucid.Theme) => ({
            marginTop: theme.space.s104,
            marginBottom: theme.space.s64,
            textAlign: headerJustify,
            '& > p': {
              margin: 0,
            },
          })}
          variant={headingVariant}
        >
          {customerLogoHeader}
        </Heading>
      )}
      {logoSection && logos && (
        <LogoSection
          height={logoSection.fieldRowHeight}
          logos={logos}
          logosColor={logoSection.fieldLogosColor as Lucid.Component.LogoSection['Color']['Variant']}
          variant={logoSection.fieldLogoLayoutVariant as Lucid.Component.LogoSection['Layout']['Variant']}
        />
      )}
      {fieldCustomerLogosLink && (
        <Box>
          <Text
            as="div"
            css={(theme: Lucid.Theme) => ({
              display: 'flex',
              justifyContent: 'center',
              marginTop: theme.space.s40,
              [mqMin.tabletLandscape]: {
                justifyContent: 'flex-end',
              },
            })}
            variant="interactiveS400"
          >
            <Link
              css={(theme: Lucid.Theme) =>
                hasDarkBackground && {
                  color: theme.colors.coolGrey[0],
                  '&:hover': {
                    color: theme.colors.coolGrey[0],
                  },
                }
              }
              iconAnimation="navigational"
              to={fieldCustomerLogosLink?.url?.path || ''}
              variant={hasDarkBackground ? 'primaryInverse' : 'primary'}
            >
              {fieldCustomerLogosLink?.title}
            </Link>
          </Text>
        </Box>
      )}
      {hasHeadingBelow && customerLogoHeader && (
        <Box>
          <Text
            as="div"
            css={(theme: Lucid.Theme) => ({
              display: 'flex',
              justifyContent: 'center',
              marginTop: theme.space.s40,
              [mqMin.tabletLandscape]: {
                justifyContent: 'flex-end',
              },
              color: theme.colors.coolGrey[100],
            })}
          >
            {customerLogoHeader}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default FlexiCustomerLogos;

export const query = graphql`
  fragment flexiCustomerLogosSection on Drupal_ParagraphFlexiCustomerLogos {
    fieldFlourishedHeading
    fieldHasThreeAcross
    fieldCustomerLogosHeader {
      processed
    }
    fieldLogosSection {
      entity {
        ... on Drupal_ParagraphExternalLogoSection {
          id
          fieldExternalLogos {
            entity {
              ... on Drupal_ParagraphExternalLogo {
                fieldLogoSlug
              }
            }
          }
          fieldLogoLayoutVariant
          fieldLogosColor
          fieldRowHeight
        }
      }
    }
    fieldCustomerLogosLink {
      title
      url {
        path
      }
    }
    fieldFlexiHeaderJustify {
      entity {
        entityLabel
      }
    }
  }
`;
